<script setup lang="ts">
import type { ShallowRef } from 'vue'
import { computed, shallowRef, watchEffect } from 'vue'
import { CheckboxIndicator, CheckboxRoot } from 'radix-vue'
import { EStates } from '../../composables/useCreateComponentConfig'
import VIcon from '../VIcon/VIcon.vue'
import ValidationMessage from '../parts/ValidationMessage.vue'
import type { ICheckbox } from './types'
import useCheckboxConfig from './composables/useCheckboxConfig'
import type { TIconSizes, TIconSizesType, TSizes } from '@/ui/ts/types/Sizes'
import { isDark } from '@/lib/utils'

const props = withDefaults(defineProps<ICheckbox>(), {
    fontVariant: 'label',
})
const emits = defineEmits<IEmits>()

interface IEmits {
    (e: 'update:modelValue', value: boolean): void

    (e: 'update:checked', value: boolean): void
}

const checkboxConfig: ShallowRef<Record<string, string>> = shallowRef({} as Record<string, string>)

watchEffect(() => {
    checkboxConfig.value = useCheckboxConfig(props)
})

const isChecked = computed({
    get: () => props.modelValue,
    set: (value: boolean) => {
        emits('update:modelValue', value)
    },
})

function updateChecked(value: boolean): void {
    isChecked.value = value
}

const combinedLabel = computed(() => {
    return props.required ? `<p>${props.label}<span class="text-validation-danger ml-1">*</span></p>` : props.label
})

const checkboxContainerStyles = computed(() => {
    const classes = {
        XSMALL: 'min-w-base w-base h-base',
        SMALL: 'min-w-base w-base h-base',
        MEDIUM: 'min-w-medium w-medium h-medium',
        LARGE: 'min-w-medium w-medium h-medium',
    }

    return classes[props.size]
})

const ICON_SIZES = {
    XSMALL: 'XSMALL',
    SMALL: 'XSMALL',
    MEDIUM: 'MEDIUM',
    LARGE: 'MEDIUM',
} as unknown as TIconSizes

const iconSize = computed((): TSizes | TIconSizesType => {
    return ICON_SIZES[props.size as any] as TSizes | TIconSizesType
})
</script>

<template>
    <div
        :class="[isDark(dark)]"
        data-gpp-tools-ui
    >
        <div class="gap-horizontal-icon-to-element flex flex-row items-start">
            <div
                class="flex items-center justify-center"
                :class="checkboxContainerStyles"
            >
                <CheckboxRoot
                    :id="id"
                    v-model="isChecked"
                    :checked="isChecked"
                    :variant="variant"
                    :size="size"
                    :aria-disabled="disabled"
                    :disabled="disabled ?? false"
                    :class="checkboxConfig.rootClasses"
                    :data-test="`checkbox-${dataTest}`"
                    @update:checked="(checked: boolean) => updateChecked(checked as boolean)"
                >
                    <CheckboxIndicator
                        :aria-disabled="disabled"
                        :disabled="disabled"
                        :aria-checked="checked"
                        :class="checkboxConfig.indicatorClasses"
                    >
                        <VIcon
                            :name="icon || 'check'"
                            :size="iconSize"
                            :dark="dark"
                        />
                    </CheckboxIndicator>
                </CheckboxRoot>
            </div>
            <div class="pt-2xs flex flex-col">
                <div class="flex items-start justify-start">
                    <slot
                        :id="id"
                        :label="combinedLabel"
                        :classes="[checkboxConfig.labelClasses, disabled ? 'cursor-default' : 'cursor-pointer']"
                    >
                        <label
                            v-html-gpp="combinedLabel"
                            :for="id"
                            :class="[checkboxConfig.labelClasses, disabled ? 'cursor-default' : 'cursor-pointer']"
                        />
                    </slot>
                </div>
                <div
                    v-if="helperMessage || (state === EStates.ERROR && errorMessage)"
                    class="flex flex-col"
                >
                    <HelperMessage
                        v-if="helperMessage"
                        :message="helperMessage"
                    />

                    <ValidationMessage
                        v-if="state === EStates.ERROR && errorMessage"
                        :icon="false"
                        :text="errorMessage"
                        :status="EStates.ERROR"
                        data-test="checkbox-error"
                        class="!p-0"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
